import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Bookmark, Trash2, Play, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError, pictureUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const PlatformBadge = ({ platform }) => {
  const label = platform === 'tiktok' ? 'TikTok' : platform === 'instagram' ? 'Instagram' : 'Refuel-video';
  const cls = platform === 'tiktok' ? 'bg-white text-black' : platform === 'instagram' ? 'bg-gradient-to-r from-pink-500 to-orange-400 text-white' : 'bg-[#ff2e2e] text-black';
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${cls}`}><Play size={10} /> {label}</span>;
};

export const SocialPostCard = ({ post, onChange, onDelete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const isAdmin = !!user?.is_admin;

  const react = async (kind) => {
    if (!user) {
      toast.info('Logg inn for å like og lagre videoar');
      navigate('/login');
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/social-posts/${post.id}/${kind}`);
      onChange({ ...post, likes: data.likes, [kind === 'like' ? 'liked' : 'favorited']: data.active });
      if (kind === 'favorite') toast.success(data.active ? 'Lagra i favorittane dine' : 'Fjerna frå favorittar');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Fjerne denne videoen?')) return;
    try {
      await api.delete(`/social-posts/${post.id}`);
      onDelete(post.id);
      toast.success('Videoen er fjerna');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const ratio = post.platform === 'instagram' ? '4 / 5' : '9 / 16';

  return (
    <article data-testid={`social-post-${post.id}`} className="rounded-3xl bg-[#1a1424] border border-white/5 overflow-hidden flex flex-col">
      <div className="relative bg-black" style={{ aspectRatio: ratio }}>
        {post.platform === 'upload' ? (
          <video
            src={pictureUrl(post.video_url)}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid="social-video"
          />
        ) : (
          <iframe
            title={post.title || `${post.platform}-video`}
            src={post.embed_url}
            className="absolute inset-0 w-full h-full"
            style={{ border: 0 }}
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture; clipboard-write"
            allowFullScreen
            data-testid="social-iframe"
          />
        )}
      </div>
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <PlatformBadge platform={post.platform} />
          <div className="flex items-center gap-1 shrink-0">
            {post.platform !== 'upload' && (
              <a href={post.url} target="_blank" rel="noreferrer noopener" title="Opne originalen" className="w-9 h-9 rounded-full border border-white/10 text-white/70 hover:text-white hover:border-white/40 flex items-center justify-center transition-colors">
                <ExternalLink size={15} />
              </a>
            )}
            {isAdmin && (
              <button onClick={remove} data-testid={`social-delete-${post.id}`} title="Fjern" className="w-9 h-9 rounded-full border border-white/10 text-white/70 hover:text-red-400 hover:border-red-400 flex items-center justify-center transition-colors">
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
        {post.title && <p className="text-white font-semibold text-sm truncate">{post.title}</p>}
        <div className="flex items-center gap-2">
          <button onClick={() => react('like')} data-testid={`social-like-${post.id}`} aria-pressed={post.liked} className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-sm font-bold transition-colors ${post.liked ? 'bg-[#ff2e2e] border-[#ff2e2e] text-black' : 'border-white/15 text-white/80 hover:border-[#ff2e2e] hover:text-[#ff2e2e]'}`}>
            <Heart size={16} className={post.liked ? 'fill-current' : ''} /> <span data-testid={`social-likes-${post.id}`}>{post.likes}</span>
          </button>
          <button onClick={() => react('favorite')} data-testid={`social-favorite-${post.id}`} aria-pressed={post.favorited} title="Lagre som favoritt" className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-sm font-bold transition-colors ${post.favorited ? 'bg-[#ffb400] border-[#ffb400] text-black' : 'border-white/15 text-white/80 hover:border-[#ffb400] hover:text-[#ffb400]'}`}>
            <Bookmark size={16} className={post.favorited ? 'fill-current' : ''} /> {post.favorited ? 'Lagra' : 'Lagre'}
          </button>
        </div>
      </div>
    </article>
  );
};
